import { useEffect, useMemo, useState } from "react"

import { FormProvider, useForm, useWatch } from "react-hook-form"

import styles from "./SmsSendingRuleMappingTab.module.scss"

import {
	useSouhlAdminMutation,
	useSouhlAdminQuery,
} from "@/provider/apollo/Apollo"
import SmsSendingRulesMappingTable from "@/souhl_admin/components/features/companies/sms-sending-rule/SmsSendingRuleMappingTab/SmsSendingRulesMappingTable"
import { graphql } from "@/souhl_admin/gql/__generated__"
import {
	CompanySmsSendingRuleMappingsForm,
	companySmsSendingRuleMappingsResolver,
} from "@/souhl_admin/utils/form/companySmsSendingRuleMappings"

const getCompanySmsSendingRulesQuery = graphql(/* GraphQL */ `
	query getCompanySmsSendingRulesV2ForMapping($companyId: String!) {
		companySmsSendingRulesV2(companyId: $companyId) {
			rules {
				id
				name
			}
		}
	}
`)

const getCompanyQuery = graphql(/* GraphQL */ `
	query getCompanyForMapping($param: CompanyParam!) {
		company(param: $param) {
			currentCompany: company {
				id
				salons {
					id
					displayName
					appliedCompanySmsSendingRule {
						id
					}
				}
			}
		}
	}
`)

const updateSalonAndCompanySmsSendingRuleMappingsMutation = graphql(
	/* GraphQL */ `
		mutation updateSalonAndCompanySmsSendingRuleMappings(
			$input: UpdateSalonAndCompanySmsSendingRuleMappingsInput!
		) {
			updateSalonAndCompanySmsSendingRuleMappings(input: $input) {
				salonIds
			}
		}
	`
)

type SmsSendingRuleMappingTabProps = {
	companyId: string
}

const SmsSendingRuleMappingTab = ({
	companyId,
}: SmsSendingRuleMappingTabProps) => {
	const { data: rulesData } = useSouhlAdminQuery(
		getCompanySmsSendingRulesQuery,
		{
			variables: { companyId },
			fetchPolicy: "cache-first",
		}
	)
	const { data: companyData, refetch: refetchCompany } = useSouhlAdminQuery(
		getCompanyQuery,
		{
			variables: { param: { id: companyId } },
			fetchPolicy: "no-cache",
		}
	)

	const rules = rulesData?.companySmsSendingRulesV2.rules || []
	const salons = companyData?.company?.currentCompany?.salons || []

	// Mapping form
	const methods = useForm<CompanySmsSendingRuleMappingsForm>({
		resolver: companySmsSendingRuleMappingsResolver,
		defaultValues: { items: [] },
	})
	const { handleSubmit, reset, control, formState, setValue, getValues } =
		methods
	const [initializedMapping, setInitializedMapping] = useState(false)

	const [updateMappings, { loading: savingMappings }] = useSouhlAdminMutation(
		updateSalonAndCompanySmsSendingRuleMappingsMutation
	)

	// Initialize form from current company data
	useEffect(() => {
		if (!initializedMapping && salons && salons.length > 0) {
			const items = salons.map((s) => ({
				salonId: s.id,
				companySmsSendingRuleId: s.appliedCompanySmsSendingRule?.id ?? "",
			}))
			reset({ items })
			setInitializedMapping(true)
		}
	}, [initializedMapping, salons, reset])

	const handleToggle = (salonId: number, ruleId: string, checked: boolean) => {
		const items = getValues("items")
		const idx = items.findIndex((i) => i.salonId === salonId)
		if (idx >= 0) {
			setValue(`items.${idx}.companySmsSendingRuleId`, checked ? ruleId : "", {
				shouldDirty: true,
			})
		}
	}

	const onSubmit = async (values: CompanySmsSendingRuleMappingsForm) => {
		if (!companyId) {
			throw new Error("companyId is required for saving mappings")
		}
		const inputs = values.items.map((i) => ({
			salonId: i.salonId,
			companySmsSendingRuleId: i.companySmsSendingRuleId,
		}))
		await updateMappings({
			variables: { input: { companyId, inputs } },
		})
		await refetchCompany()
		alert("更新しました")
		reset(values) // clear dirty state to current persisted values
	}

	const items = useWatch({ control, name: "items" }) || []
	const valueRecord: Record<number, string | null> = useMemo(() => {
		const r: Record<number, string | null> = {}
		items.forEach((i) => {
			r[i.salonId] = i.companySmsSendingRuleId || null
		})
		return r
	}, [items])

	const errorSalonIds = useMemo(() => {
		// FIXME: アサーションせずにundefinedを判定してsentryで検知できる様にしたいがフォーム定義を変えてもtypeErrorを消せない
		const errs = formState.errors?.items as
			| ({ companySmsSendingRuleId?: { message?: string } } | undefined)[]
			| undefined
		if (!errs) return []
		const ids: number[] = []
		errs.forEach((e, idx) => {
			if (e?.companySmsSendingRuleId) {
				const item = items[idx]
				if (item) ids.push(item.salonId)
			}
		})
		return ids
	}, [formState.errors, items])

	if (!salons || !rules) return null

	return (
		<FormProvider {...methods}>
			<form
				className={styles.tab_inner_content}
				onSubmit={handleSubmit(onSubmit)}
			>
				{salons.length === 0 ? (
					<div>店舗がありません。</div>
				) : rules.length === 0 ? (
					<div>配信ルールがありません。</div>
				) : (
					<>
						<div className={styles.top_actions}>
							<button
								type="submit"
								className={styles.save_button}
								disabled={savingMappings}
							>
								保存
							</button>
						</div>
						{errorSalonIds.length > 0 && (
							<div className={styles.mapping_error}>
								未選択の店舗があります。各店舗にルールを1つ選択してください。
							</div>
						)}
						<SmsSendingRulesMappingTable
							salons={salons}
							rules={rules}
							value={valueRecord}
							onChange={handleToggle}
							errorSalonIds={errorSalonIds}
						/>
					</>
				)}
			</form>
		</FormProvider>
	)
}

export default SmsSendingRuleMappingTab
